import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  Building2,
  Bell,
  Check,
  ChevronDown,
  CalendarDays,
  LogOut,
  Mail,
  Menu,
  Info,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  Avatar,
  Breadcrumbs,
  DropdownItem,
  IconButton,
  Sidebar,
  SidebarBrand,
  SidebarFooter,
  SidebarItem,
  SidebarSection,
  UserMenu,
  NotificationBadge,
} from './CanonicalDashboard';
import { SearchField } from './DataTable';

export type AppNavigationItem = Readonly<{
  id: string;
  label: string;
  href: string;
  icon: LucideIcon;
  group?: string;
  keywords?: readonly string[];
}>;
export type AppBreadcrumb = Readonly<{ label: string; href?: string }>;
export type AppUser = Readonly<{ name: string; detail: string; initials: string }>;

export function AppShell({
  navigation,
  activeId,
  breadcrumbs,
  user,
  onNavigate,
  onLogout,
  notifications,
  notificationCount = 0,
  organization,
  release,
  children,
}: {
  navigation: readonly AppNavigationItem[];
  activeId: string;
  breadcrumbs: readonly AppBreadcrumb[];
  user: AppUser;
  onNavigate?: () => void;
  onLogout?: () => void;
  notifications?: ReactNode;
  notificationCount?: number;
  organization?: Readonly<{
    items: readonly Readonly<{ id: string; name: string; code: string }>[];
    activeId: string;
    onChange: (id: string) => void;
  }>;
  release?: Readonly<{ version: string; href: string }>;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [globalQuery, setGlobalQuery] = useState('');
  const [utilityMessage, setUtilityMessage] = useState('');
  const [alertsOpen, setAlertsOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const [mobileMode, setMobileMode] = useState(
    () => window.matchMedia?.('(max-width: 800px)').matches ?? false,
  );
  const navigationExpanded = mobileMode ? mobileOpen : !collapsed;
  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 800px)');
    if (!media) return;
    const updateMode = (event: MediaQueryListEvent) => {
      setMobileMode(event.matches);
      setMobileOpen(false);
    };
    setMobileMode(media.matches);
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', updateMode);
      return () => media.removeEventListener('change', updateMode);
    }
    media.addListener?.(updateMode);
    return () => media.removeListener?.(updateMode);
  }, []);
  useEffect(() => {
    const close = () => {
      setMobileOpen(false);
      setAlertsOpen(false);
    };
    window.addEventListener('hashchange', close);
    return () => window.removeEventListener('hashchange', close);
  }, []);
  useEffect(() => {
    if (!alertsOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!notificationsRef.current?.contains(event.target as Node)) setAlertsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAlertsOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [alertsOpen]);
  const toggleNavigation = () => {
    if (mobileMode) setMobileOpen((value) => !value);
    else setCollapsed((value) => !value);
  };
  const closeMobile = () => {
    setMobileOpen(false);
    onNavigate?.();
  };
  const searchApplication = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const firstResult = globalResults[0];
    if (!firstResult) return;
    window.location.hash = firstResult.href;
    setGlobalQuery('');
    closeMobile();
  };
  const normalizedQuery = globalQuery.trim().toLocaleLowerCase('es');
  const globalResults = useMemo(
    () =>
      normalizedQuery
        ? navigation
            .filter((item) =>
              [item.label, item.id, item.href, ...(item.keywords ?? [])]
                .join(' ')
                .toLocaleLowerCase('es')
                .includes(normalizedQuery),
            )
            .slice(0, 7)
        : [],
    [navigation, normalizedQuery],
  );
  return (
    <div
      className={`ui-shell${collapsed ? ' ui-shell--collapsed' : ''}${mobileOpen ? ' ui-shell--mobile-open' : ''}`}
    >
      <a className="ui-skip-link" href="#main-content">
        Saltar al contenido
      </a>
      <Sidebar className="ui-sidebar">
        <button
          className="ui-sidebar-close"
          type="button"
          aria-label="Cerrar menú lateral"
          onClick={() => setMobileOpen(false)}
        >
          <X aria-hidden="true" />
        </button>
        <SidebarBrand
          className="ui-brand"
          logo={
            <>
              <img
                className="ui-brand-logo ui-brand-logo--full"
                src="/brand/urbanik-horizontal-on-dark.png"
                alt="URBANIK ERP"
              />
              <img
                className="ui-brand-logo ui-brand-logo--compact"
                src="/brand/urbanik-icon-light.png"
                alt="URBANIK"
              />
            </>
          }
        />
        <div className="ui-sidebar-scroll">
          {[...new Set(navigation.map((item) => item.group ?? 'Principal'))].map((group) => (
            <SidebarSection key={group} title={group} className="ui-app-sidebar-section">
              {navigation
                .filter((item) => (item.group ?? 'Principal') === group)
                .map((item) => (
                  <SidebarItem
                    key={item.id}
                    href={item.href}
                    className={
                      item.id === activeId ? 'ui-nav-link ui-nav-link--active' : 'ui-nav-link'
                    }
                    active={item.id === activeId}
                    icon={<item.icon aria-hidden="true" />}
                    label={item.label}
                    title={collapsed ? item.label : undefined}
                    onClick={closeMobile}
                  />
                ))}
            </SidebarSection>
          ))}
          <SidebarFooter className="ui-app-sidebar-footer">
            <div className="ui-sidebar-phase">
              <span>{release ? 'URBANIK · v' + release.version : 'URBANIK - 2026'}</span>
            </div>
            {onLogout ? (
              <SidebarItem
                href="#login"
                className="ui-nav-link ui-sidebar-logout"
                icon={<LogOut aria-hidden="true" />}
                label="Cerrar sesión"
                title={collapsed ? 'Cerrar sesión' : undefined}
                onClick={onLogout}
              />
            ) : null}
          </SidebarFooter>
        </div>
      </Sidebar>
      {mobileOpen ? (
        <button
          className="ui-backdrop"
          type="button"
          aria-label="Cerrar navegación"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}
      <div className="ui-workspace">
        <header className={`ui-topbar${organization ? ' ui-topbar--with-organization' : ''}`}>
          <IconButton
            aria-label={navigationExpanded ? 'Contraer navegación' : 'Expandir navegación'}
            label={navigationExpanded ? 'Contraer navegación' : 'Expandir navegación'}
            aria-expanded={navigationExpanded}
            onClick={toggleNavigation}
          >
            <Menu aria-hidden="true" />
          </IconButton>
          <Breadcrumbs items={breadcrumbs} label="Breadcrumbs" />
          <img
            className="ui-topbar-brand"
            src="/brand/urbanik-horizontal-clear-cropped.png"
            alt="URBANIK"
          />
          <form className="ui-topbar-search" role="search" onSubmit={searchApplication}>
            <SearchField
              label="Buscar en toda la aplicación"
              placeholder="Buscar módulos, vistas y herramientas…"
              value={globalQuery}
              onChange={setGlobalQuery}
            />
            {normalizedQuery ? (
              <div className="ui-app-search-results" aria-label="Resultados de búsqueda">
                {globalResults.length ? (
                  globalResults.map((item) => (
                    <a key={item.id} href={item.href} onClick={() => setGlobalQuery('')}>
                      <item.icon aria-hidden="true" />
                      <span>
                        <strong>{item.label}</strong>
                        <small>Abrir módulo</small>
                      </span>
                    </a>
                  ))
                ) : (
                  <p>No se encontraron módulos accesibles.</p>
                )}
              </div>
            ) : null}
          </form>
          {organization ? (
            <details className="ui-organization-selector">
              <summary aria-label="Entidad activa">
                <Building2 aria-hidden="true" />
                <span>Entidad</span>
                <strong>{organization.items.find((item) => item.id === organization.activeId)?.code === 'BASELINE_COMIVALLE' ? 'COMIVALLE' : organization.items.find((item) => item.id === organization.activeId)?.name}</strong>
                <ChevronDown aria-hidden="true" />
              </summary>
              <div className="ui-organization-selector__menu" role="menu" aria-label="Seleccionar entidad">
                {organization.items.map((item) => {
                  const selected = item.id === organization.activeId;
                  return <button type="button" role="menuitemradio" aria-checked={selected} key={item.id} onClick={(event) => { event.currentTarget.closest('details')?.removeAttribute('open'); if (!selected) organization.onChange(item.id); }}><Building2 aria-hidden="true" /><span><strong>{item.code === 'BASELINE_COMIVALLE' ? 'COMIVALLE' : item.name}</strong><small>{selected ? 'Entidad activa' : 'Cambiar a esta entidad'}</small></span>{selected ? <Check aria-hidden="true" /> : null}</button>;
                })}
              </div>
            </details>
          ) : null}
          <div className="ui-topbar-tools">
            <div ref={notificationsRef} className="ui-notifications">
              <IconButton
                label="Alertas"
                badge={
                  notificationCount ? (
                    <NotificationBadge>{notificationCount}</NotificationBadge>
                  ) : undefined
                }
                aria-expanded={alertsOpen}
                onClick={() => setAlertsOpen((value) => !value)}
              >
                <Bell aria-hidden="true" />
              </IconButton>
              {alertsOpen ? (
                <aside className="ui-notifications__panel" aria-label="Notificaciones">
                  <header>
                    <strong>Notificaciones</strong>
                    <button type="button" onClick={() => setAlertsOpen(false)}>
                      Cerrar
                    </button>
                  </header>
                  {notifications ?? <p>No hay alertas nuevas.</p>}
                </aside>
              ) : null}
            </div>
            <IconButton
              label="Mensajes"
              onClick={() => setUtilityMessage('No hay mensajes nuevos.')}
            >
              <Mail aria-hidden="true" />
            </IconButton>
            <IconButton
              label="Calendario"
              onClick={() =>
                setUtilityMessage('El calendario estará disponible en una fase posterior.')
              }
            >
              <CalendarDays aria-hidden="true" />
            </IconButton>
            <div className="ui-topbar-user">
              <UserMenu
                avatar={<Avatar initials={user.initials} size="small" status="online" />}
                name={user.name}
                detail={user.detail}
              >
                {release ? (
                  <DropdownItem
                    icon={<Info aria-hidden="true" />}
                    onSelect={() => {
                      window.location.hash = release.href;
                    }}
                  >
                    Versión y novedades
                  </DropdownItem>
                ) : null}
                <DropdownItem icon={<LogOut aria-hidden="true" />} onSelect={() => onLogout?.()}>
                  {onLogout ? 'Cerrar sesión' : 'Cerrar'}
                </DropdownItem>
              </UserMenu>
            </div>
          </div>
          <span className="sr-only" aria-live="polite">
            {utilityMessage}
          </span>
        </header>
        <main id="main-content" className="ui-main" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}
